import { useMemo } from 'react'

import { parseUnits, zeroAddress } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { useTransactionCost } from '~/hooks/transactions/useTransactionCost'
import { useTransactionFormTransferToken } from '~/hooks/transactions/useTransactionFormTransferToken'
import { useSmartAccountBalances } from '~/hooks/useBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { BasicTransactionType } from '~/types'

/**
 * Hook to check if the user has enough gas to complete the transaction,
 * in the case that the transaction is not sponsored and the user is transferring ETH.
 */
export const useInsufficientGasWarning = ({
  smartAccountId,
  chainId,
  transactionTypes,
  transactionCost,
  isSponsored,
}: {
  smartAccountId: string
  chainId: SupportedChainId
  transactionTypes: BasicTransactionType[]
  transactionCost: ReturnType<typeof useTransactionCost>
  isSponsored: boolean
}) => {
  const { smartAccounts } = useSmartAccounts()
  const senderAccount = smartAccounts.find(
    (account) => account.id === smartAccountId,
  )

  const { balances } = useSmartAccountBalances(senderAccount?.address, {
    showSpamTokens: true,
  })
  const nativeTokenBalance = balances.find(
    (balance) => balance.chainId === chainId && balance.address === zeroAddress,
  )?.amount

  const transferToken = useTransactionFormTransferToken(transactionTypes)

  const showWarning = useMemo(() => {
    // Must be transferring ETH and transaction not sponsored
    if (
      !transferToken ||
      isSponsored ||
      transferToken.token !== zeroAddress ||
      !transferToken.amount ||
      !transactionCost.transactionGas ||
      nativeTokenBalance === undefined
    )
      return false

    const tokenAmount = parseUnits(transferToken.amount, 18) // ETH has 18 decimals

    // Check if balance - transaction cost - ETH transfer amount < 0
    // Allow for 5% buffer on gas cost to account for gas price fluctuations
    const gasWithMargin = (transactionCost.transactionGas * 105n) / 100n
    const showWarning = nativeTokenBalance - gasWithMargin - tokenAmount < 0n

    return showWarning
  }, [
    nativeTokenBalance,
    isSponsored,
    transactionCost.transactionGas,
    transferToken,
  ])

  return { showWarning }
}
